'use client';

import { Input } from '@ui/components/input';
import { text } from '@modules/garden-planner/text';
import { hasPlantImage } from '@modules/plants/business_logic';
import {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  startTransition,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

import type { Plant } from '@modules/garden-planner/business_logic';
import type { PlantOption } from '@modules/garden-planner/garden_planner_views';
import { FaIcon } from '@micks-yard/icons/FaIcon';
import { faChevronDown } from '@micks-yard/icons/solid/faChevronDown';
import { faXmark } from '@micks-yard/icons/solid/faXmark';

export type { PlantOption };

interface PlantSelectorViewProps {
  plantOptions: PlantOption[];
  selectedPlant: string | null;
  selectedPlantData: Plant | null;
  plantCounts: Record<string, number>;
  showTitle?: boolean;
  autoOpen?: boolean;
  onPlantSelect: (plantId: string | null) => void;
}

const LIST_MAX_HEIGHT = 240;
const ROW_HEIGHT = 48;

function PlantIcon({
  plant,
  size = 26,
  lazy = false,
}: {
  plant: Plant;
  size?: number;
  lazy?: boolean;
}) {
  return (
    <div
      className="flex shrink-0 items-center justify-center"
      style={{ width: size, height: size }}>
      {hasPlantImage(plant.imagePath) ? (
        <img
          src={plant.imagePath}
          alt={plant.name}
          width={size}
          height={size}
          loading={lazy ? 'lazy' : undefined}
          style={{ width: size, height: size, borderRadius: 4, objectFit: 'contain' }}
        />
      ) : (
        <span style={{ fontSize: size * 0.8, lineHeight: `${size * 0.8}px` }}>{plant.emoji}</span>
      )}
    </div>
  );
}

const PlantSelectorOptionRow = memo(function PlantSelectorOptionRow({
  option,
  isSelected,
  onSelect,
}: {
  option: PlantOption;
  isSelected: boolean;
  onSelect: (value: string) => void;
}) {
  const handleClick = useCallback(() => {
    onSelect(option.value);
  }, [onSelect, option.value]);

  return (
    <div
      role="option"
      aria-selected={isSelected}
      onClick={handleClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 8px',
        width: '100%',
        cursor: 'pointer',
        backgroundColor: isSelected ? 'var(--color-gray3, rgb(243 244 246))' : 'transparent',
        boxSizing: 'border-box',
        height: ROW_HEIGHT,
      }}>
      <PlantIcon plant={option.plant} size={24} lazy />
      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <div
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: 14,
            lineHeight: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}>
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
            {option.label}
          </span>
          {option.plant.isUserOwned ? (
            <span
              style={{
                flexShrink: 0,
                fontSize: 10,
                lineHeight: '14px',
                color: 'var(--color-green10, rgb(22 163 74))',
                backgroundColor: 'var(--color-green3, rgb(220 252 231))',
                padding: '1px 6px',
                borderRadius: 4,
              }}>
              {text.yourPlantBadge}
            </span>
          ) : null}
        </div>
        {option.speciesName ? (
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 12,
              lineHeight: '16px',
              color: 'var(--color-gray9, rgb(107 114 128))',
            }}>
            {option.speciesName}
          </div>
        ) : null}
      </div>
    </div>
  );
});

interface PlantSelectorTriggerProps {
  selectedOption: PlantOption | undefined;
  selectedPlant: string | null;
  open: boolean;
  onToggle: () => void;
  onClear: (event: { stopPropagation?: () => void }) => void;
}

const PlantSelectorTrigger = memo(function PlantSelectorTrigger({
  selectedOption,
  selectedPlant,
  open,
  onToggle,
  onClear,
}: PlantSelectorTriggerProps) {
  const handleClearClick = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onClear(event);
    },
    [onClear]
  );

  return (
    <div
      className="plant-selector-trigger"
      role="combobox"
      aria-expanded={open}
      aria-haspopup="listbox">
      <button type="button" className="plant-selector-trigger__value" onClick={onToggle}>
        {selectedOption ? (
          <>
            <PlantIcon plant={selectedOption.plant} size={24} />
            <div className="plant-selector-trigger__label">
              <div className="plant-selector-trigger__label-primary">{selectedOption.label}</div>
              {selectedOption.speciesName ? (
                <div className="plant-selector-trigger__label-secondary">
                  {selectedOption.speciesName}
                </div>
              ) : null}
            </div>
          </>
        ) : (
          <span className="plant-selector-trigger__placeholder">{text.searchOrSelect}</span>
        )}
      </button>

      <div className="plant-selector-trigger__actions">
        {selectedPlant ? (
          <button
            type="button"
            className="plant-selector-trigger__clear"
            onClick={handleClearClick}
            aria-label={text.clearSelection}>
            <FaIcon icon={faXmark} className="size-3.5" />
          </button>
        ) : null}
        <button
          type="button"
          className="plant-selector-trigger__toggle"
          onClick={onToggle}
          aria-label={open ? text.searchPlants : text.searchOrSelect}>
          <FaIcon
            icon={faChevronDown}
            className={`size-4 plant-selector-trigger__chevron${open ? 'plant-selector-trigger__chevron--open' : ''}`}
          />
        </button>
      </div>
    </div>
  );
});

function filterPlantOptions(plantOptions: PlantOption[], search: string) {
  if (!search) return plantOptions;
  const normalizedSearch = search.toLowerCase();
  return plantOptions.filter(
    (option) =>
      option.label.toLowerCase().includes(normalizedSearch) ||
      option.value.toLowerCase().includes(normalizedSearch) ||
      option.speciesName?.toLowerCase().includes(normalizedSearch)
  );
}

interface PlantSelectorListProps {
  listId: string;
  plantOptions: PlantOption[];
  deferredSearch: string;
  selectedPlant: string | null;
  onSelect: (value: string) => void;
}

function PlantSelectorList({
  listId,
  plantOptions,
  deferredSearch,
  selectedPlant,
  onSelect,
}: PlantSelectorListProps) {
  const parentRef = useRef<HTMLDivElement>(null);

  const filteredOptions = useMemo(
    () => filterPlantOptions(plantOptions, deferredSearch),
    [plantOptions, deferredSearch]
  );

  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 5,
  });

  if (filteredOptions.length === 0) {
    return <p className="text-muted-foreground p-2 text-center text-sm">{text.noPlantsFound}</p>;
  }

  return (
    <div
      id={listId}
      ref={parentRef}
      role="listbox"
      style={{
        maxHeight: LIST_MAX_HEIGHT,
        overflow: 'auto',
      }}>
      <div
        style={{
          height: virtualizer.getTotalSize(),
          width: '100%',
          position: 'relative',
        }}>
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const option = filteredOptions[virtualRow.index];
          return (
            <div
              key={option.value}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: virtualRow.size,
                transform: `translateY(${virtualRow.start}px)`,
              }}>
              <PlantSelectorOptionRow
                option={option}
                isSelected={option.value === selectedPlant}
                onSelect={onSelect}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface PlantSelectorDropdownProps {
  listId: string;
  plantOptions: PlantOption[];
  selectedPlant: string | null;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  listReady: boolean;
  onClose: () => void;
  onSelect: (value: string) => void;
}

function PlantSelectorDropdown({
  listId,
  plantOptions,
  selectedPlant,
  searchInputRef,
  listReady,
  onClose,
  onSelect,
}: PlantSelectorDropdownProps) {
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);

  return (
    <div className="border-border flex flex-col border-t">
      <div className="flex flex-row px-2 py-2">
        <Input
          ref={searchInputRef}
          className="h-7 flex-1"
          placeholder={text.searchPlants}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          aria-autocomplete="list"
          aria-controls={listId}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              onClose();
              searchInputRef.current?.blur();
            }
          }}
        />
      </div>
      {listReady ? (
        <PlantSelectorList
          listId={listId}
          plantOptions={plantOptions}
          deferredSearch={deferredSearch}
          selectedPlant={selectedPlant}
          onSelect={onSelect}
        />
      ) : null}
    </div>
  );
}

const PlantSelectorPlantedBadge = memo(function PlantSelectorPlantedBadge({
  selectedPlantData,
  plantCounts,
}: {
  selectedPlantData: Plant;
  plantCounts: Record<string, number>;
}) {
  const count = plantCounts[selectedPlantData.id] ?? 0;

  return (
    <div className="flex flex-row items-center gap-2 px-1 py-1">
      {count > 0 ? (
        <span className="inline-flex shrink-0 items-center rounded-full border border-green-300 bg-green-100 px-2 py-1 text-sm font-semibold text-green-800">
          {text.plantedCount(count)}
        </span>
      ) : (
        <span className="border-border bg-muted text-muted-foreground inline-flex shrink-0 items-center rounded-full border px-2 py-1 text-sm font-semibold">
          {text.nonePlanted}
        </span>
      )}
    </div>
  );
});

export function PlantSelectorView({
  plantOptions,
  selectedPlant,
  selectedPlantData,
  plantCounts,
  showTitle = false,
  autoOpen = false,
  onPlantSelect,
}: PlantSelectorViewProps) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [listReady, setListReady] = useState(false);

  const selectedOption = useMemo(
    () => plantOptions.find((option) => option.value === selectedPlant),
    [plantOptions, selectedPlant]
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    setListReady(false);
  }, []);

  const handleToggle = useCallback(() => {
    setOpen((isOpen) => {
      if (isOpen) {
        setListReady(false);
        return false;
      }
      startTransition(() => {
        setListReady(true);
      });
      requestAnimationFrame(() => {
        searchInputRef.current?.focus();
      });
      return true;
    });
  }, []);

  const handleSelect = useCallback(
    (optionValue: string) => {
      onPlantSelect(optionValue === selectedPlant ? null : optionValue);
      handleClose();
    },
    [handleClose, onPlantSelect, selectedPlant]
  );

  const handleClear = useCallback(
    (event: { stopPropagation?: () => void }) => {
      event.stopPropagation?.();
      onPlantSelect(null);
    },
    [onPlantSelect]
  );

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: globalThis.MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open, handleClose]);

  useEffect(() => {
    if (!autoOpen) return;
    setOpen(true);
    startTransition(() => {
      setListReady(true);
    });
    requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
  }, [autoOpen]);

  return (
    <div className={`flex flex-col${showTitle ? 'gap-3' : ''}`}>
      {showTitle ? (
        <p className="text-foreground text-sm font-semibold">{text.plantSelectorTitle}</p>
      ) : null}

      <div className="flex flex-col gap-3">
        <div ref={rootRef}>
          <div className="border-border bg-background overflow-hidden rounded-lg border">
            <PlantSelectorTrigger
              selectedOption={selectedOption}
              selectedPlant={selectedPlant}
              open={open}
              onToggle={handleToggle}
              onClear={handleClear}
            />

            {open ? (
              <PlantSelectorDropdown
                listId={listId}
                plantOptions={plantOptions}
                selectedPlant={selectedPlant}
                searchInputRef={searchInputRef}
                listReady={listReady}
                onClose={handleClose}
                onSelect={handleSelect}
              />
            ) : null}
          </div>
        </div>

        {selectedPlantData ? (
          <PlantSelectorPlantedBadge
            selectedPlantData={selectedPlantData}
            plantCounts={plantCounts}
          />
        ) : null}
      </div>
    </div>
  );
}

export { filterPlantOptions, ROW_HEIGHT, LIST_MAX_HEIGHT };
